import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NoopValueAccessorDirective } from '@rp/shared/directives';
import { FileUtil, injectNgControl, StringUtils } from '@rp/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploaderEntity, MaxLength } from '@rp/shared/models';

import { IconComponent, IconName } from '../icon';
import { ToastService } from '../toaster';
import { FileUploaderAcceptFileType, FileUploaderIconNameByExtension } from './models';

@Component({
  selector: 'rp-file-uploader',
  standalone: true,
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [NoopValueAccessorDirective],
  imports: [IconComponent, TranslateModule],
  host: {
    '[class.file-uploaded]': 'fileInputEl().nativeElement.value || ngControl.control.value',
    '[class.dragging]': 'isDragging()',
    '[class.clickable]': 'isRectangleClickable()',
    '(click)': 'emitFileInputClick()',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class FileUploaderComponent implements OnInit {
  fileInputEl = viewChild<ElementRef>('fileInput');
  ngControl = injectNgControl();

  isMulti = input(false, { transform: booleanAttribute });
  acceptTypes = input(FileUploaderAcceptFileType.DOCUMENT);
  acceptTypesHint = input('');
  allowFileReplace = input(false, { transform: booleanAttribute });
  maxSize = input('10Mb');
  maxFileNameLength = input(MaxLength);
  showEyeIconOnHover = input(false, { transform: booleanAttribute });

  fileRemoved = output<number | null>();
  eyeIconClicked = output<void>();

  errors = signal<string | null>(null);
  file = signal<FileUploaderEntity>(null);
  isDragging = signal(false);
  isHovering = signal(false);
  isLoading = signal<boolean>(false);

  readonly iconNameByExtension = FileUploaderIconNameByExtension;
  readonly icons = IconName;

  isRectangleClickable = computed(
    () => (!this.file() || this.allowFileReplace()) && !this.showEyeIcon(),
  );
  maxSizeUnit = computed(() => {
    const value = parseInt(this.maxSize());

    return this.maxSize().split(String(value))[1];
  });
  maxSizeView = computed(() => parseInt(this.maxSize()));
  showEyeIcon = computed(
    () => this.showEyeIconOnHover() && this.file()?.fileId && this.isHovering(),
  );

  private acceptedFileTypesSet = computed(() => new Set(this.acceptTypes().split(',')));
  private maxSizeInBytes = computed(() => StringUtils.stringSizeToBytes(this.maxSize()));
  private _cdRef = inject(ChangeDetectorRef);
  private _toast = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.file()) {
        this.ngControl.control.setValue(this.file());
        this._cdRef.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    if (this.ngControl.value) {
      this.file.set(this.ngControl.value);
    }

    this.ngControl.control.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(newValue => {
        if (newValue === null) {
          this.file.set(null);
        }
        if (this.file()?.fileId !== newValue?.fileId) {
          this.file.set(newValue);
        }
      });

    this.ngControl.control.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdRef.detectChanges());
  }

  emitFileInputClick(): void {
    if (!this.isRectangleClickable()) {
      return;
    }

    this.fileInputEl().nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    if (this.file() && !this.allowFileReplace()) {
      return;
    }
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    if (this.file() && !this.allowFileReplace()) {
      return;
    }
    event.preventDefault();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.file() && !this.allowFileReplace()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const file = event.dataTransfer?.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  onMouseEnter(): void {
    this.isHovering.set(true);
  }

  onMouseLeave(): void {
    this.isHovering.set(false);
  }

  handleFile(file: File): void {
    const fileExtension = FileUtil.getExtensionFromName(file.name);
    if (
      file?.size > this.maxSizeInBytes() ||
      !this.acceptedFileTypesSet().has('.' + fileExtension)
    ) {
      return this._uploadErrorHandler();
    }

    if (file?.name.length > this.maxFileNameLength()) {
      return this._fileNameErrorHandler();
    }

    this.file.set({
      fileName: file.name,
      fileExtension,
      newFile: file,
    });
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files[0];

    if (file) {
      this.handleFile(file);
    }
  }

  onRemove(event: Event): void {
    event.stopPropagation();

    this.ngControl.control.reset();
    this.ngControl.control.markAsTouched();

    this.fileRemoved.emit(this.file()?.fileId);

    this.file.set(null);
    this.fileInputEl().nativeElement.value = '';
  }

  onNoFileRemove(event: Event): void {
    event.stopPropagation();

    this.fileRemoved.emit(null);
  }

  private _uploadErrorHandler(): void {
    this.ngControl.control.markAsDirty();

    this._toast.showToast({
      title: this._translateService.instant('general.uploadFailed'),
      type: 'error',
      text: this._translateService.instant('general.uploadFailedMessage'),
    });

    this.fileInputEl().nativeElement.value = '';
  }

  private _fileNameErrorHandler(): void {
    this.ngControl.control.markAsDirty();

    this._toast.showToast({
      title: this._translateService.instant('general.uploadFailed'),
      type: 'error',
      text: this._translateService.instant('general.uploadFailedNameMessage', {
        maxLength: this.maxFileNameLength(),
      }),
    });

    this.fileInputEl().nativeElement.value = '';
  }
}
